import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import _map from 'lodash.map'
import styled from 'styled-components'
import { axios, context as AuthContext } from '../../context/auth'

import { canEdit as canEditUtil, isOwner as isOwnerUtil } from '../../utils/auth'
import { event, pageView, ACTIONS, PAGES, CATEGORIES } from '../../utils/analytics-tracker'
import windowStorage from '../../utils/window-storage'

import { addRecentSetlist } from '../../utils/recents'

import computeNumbering from '../../utils/compute-numbering'

import CopySetList from './copy-setlist'
import SetlistShareButton from './share-button'
import confirm from '../confirmable'
import Duration from './duration'
import PermissionsModal from '../perms/permissions-modal'
import Song from '../songs/song'
import RehearsalFollowerBar from '../rehearse/follower-bar'

import { ConfirmFocal } from '../../styled/confirm-modal'
import TitleGroup from '../../styled/title-group'
import MetaSelectContainer from '../../styled/meta-select-container'
import Loader from '../../styled/loader'
import Select from '../../styled/select'

import { context as RehearsalContext } from '../../context/rehearsal'

export const ADD_FIELDS = [
  { label: 'None', key: '' },
  { label: 'Tempo', key: 'tempo' },
  { label: 'First Chord', key: 'first' },
  { label: 'Key', key: 'key' },
  { label: 'Duration', key: 'duration' },
  { label: 'Last Rehearsed', key: 'lastRehearsed' },
  { label: 'Meta 1', key: 'meta1' },
  { label: 'Meta 2', key: 'meta2' },
  { label: 'Meta 3', key: 'meta3' },
  { label: 'Meta 4', key: 'meta4' }
]

const objectDisplay = obj => obj.title || obj.email

const confirmMessage = (removeObj) => {
  return (
    <div>Are you sure you want delete the set list
      <ConfirmFocal>{objectDisplay(removeObj)}</ConfirmFocal>
      ?
    </div>
  )
}

const BreakSeparator = styled.div`
  display: flex;
  align-items: center;
  margin: 1em 0;
  color: ${({ theme }) => theme.bodyText};
  opacity: 0.7;
  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px dashed ${({ theme }) => theme.borderColor};
  }
  &::before { margin-right: 10px; }
  &::after { margin-left: 10px; }
`

const NoteText = styled.div`
  padding: 0.5em 1em;
  font-style: italic;
  color: ${({ theme }) => theme.bodyText};
  opacity: 0.8;
`

const getEntries = (setlist) => {
  if (setlist.entries && setlist.entries.length > 0) {
    return setlist.entries.map(e => {
      if (e.song) {
        const songId = e.song._id || e.song
        const songObj = (setlist.songs || []).find(s => (s._id || s) === songId)
        return songObj || { _id: songId }
      }
      return e
    })
  }
  return setlist.songs || []
}

const ViewSetList = ({ setContext: legacySetContext, setlistid }) => {
  const navigate = useNavigate()
  const { setlistid: matchSetlistId } = useParams()
  const useSetlistId = setlistid || matchSetlistId

  const { user, loading: authLoading } = useContext(AuthContext)
  const { isOwner: isRehearsalOwner, isFollowing, setContext } = useContext(RehearsalContext)

  // Redirect to login if not authenticated (but wait for auth to finish loading)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login')
    }
  }, [user, authLoading, navigate])

  const [setlist, setSetlist] = useState()
  const [addFieldKey, setAddFieldKey] = useState(windowStorage.getItem('songMetaFieldName') || '')
  const [loading, setLoading] = useState(true)
  const [canEdit, setCanEdit] = useState(false)
  const [isOwner, setIsOwner] = useState(false)
  const [permissionError, setPermissionError] = useState(false)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  const refreshSetList = useCallback(async () => {
    if (!useSetlistId) return
    setLoading(true)
    setPermissionError(false)
    try {
      const response = await axios.get(`/api/setlists/${useSetlistId}`)
      setSetlist(response.data)
      addRecentSetlist(useSetlistId, response.data.title)
      setCanEdit(canEditUtil(response.data))
      setIsOwner(isOwnerUtil(response.data))
    } catch (err) {
      console.log('Setlist fetch error:', err)
      console.log('Error response:', err.response)
      console.log('Error status:', err.response?.status)
      // Check if it's a permission error (403 or 404)
      if (err.response && (err.response.status === 403 || err.response.status === 404)) {
        console.log('Setting permission error to true')
        setPermissionError(true)
      }
    } finally {
      console.log('Setting loading to false')
      setLoading(false)
    }
  }, [useSetlistId])

  const copySetList = useCallback(async () => {
    try {
      const response = await axios.get(`/api/setlists/${useSetlistId}/copy`)
      navigate(`/setlists/${response.data._id}/edit`)
    } catch (err) {
      console.log(err)
    }
  })

  const deleteSetList = useCallback(() => {
    confirm(confirmMessage(setlist)).then(
      (result) => {
        axios({
          url: `/api/setlists/${setlist._id}`,
          method: 'DELETE'
        }).then(() => {
          event(CATEGORIES.SONG, ACTIONS.SETLIST_DELETE)
          navigate('/setlists')
        })
      },
      (result) => {
        event(CATEGORIES.SONG, ACTIONS.SETLIST_DELETE_CANCEL)
      }
    )
  })

  const onAddFieldChange = (value) => {
    windowStorage.setItem('songMetaFieldName', value)
    setAddFieldKey(value)
  }

  const clickEdit = () => {
    navigate(`/setlists/${useSetlistId}/edit`)
  }

  const clickExport = () => {
    navigate(`/setlists/${useSetlistId}/export`)
  }

  useEffect(() => {
    // Only fetch setlist after auth is complete and user is available
    if (!authLoading && user) {
      pageView(PAGES.SETLIST_VIEW)
      refreshSetList()
      if (legacySetContext) {
        legacySetContext({
          songId: undefined,
          setlistId: useSetlistId
        })
      }
    }
  }, [useSetlistId, authLoading, user, refreshSetList, legacySetContext])

  // Update rehearsal context when owner views this setlist
  useEffect(() => {
    if (isRehearsalOwner && useSetlistId) {
      setContext(null, useSetlistId)
    }
  }, [isRehearsalOwner, useSetlistId])

  console.log('SetListView render - permissionError:', permissionError, 'loading:', loading, 'setlist:', !!setlist)

  // Show permission error first (before checking loading or setlist validity)
  if (permissionError) {
    console.log('Showing permission error')
    return (
      <div className='ViewSetList'>
        {isFollowing && <RehearsalFollowerBar />}
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h3>Permission Required</h3>
          <p>You don't have permission to view this setlist. The rehearsal owner has navigated to content you cannot access.</p>
        </div>
      </div>
    )
  }

  // Show loading state
  if (loading || !setlist) {
    console.log('Showing loading state')
    return (
      <div className='ViewSetList'>
        {isFollowing && <RehearsalFollowerBar />}
        <div>loading</div>
      </div>
    )
  }

  // Show loading while auth is in progress
  if (authLoading) {
    return <Loader />
  }

  return (
    <div className='ViewSetList'>
      <RehearsalFollowerBar />
      <TitleGroup
        title={setlist.title}
        onEdit={canEdit && clickEdit}
        onPermissions={() => setShowPermissionsModal(true)}
        onDelete={isOwner && deleteSetList}
        onExport={clickExport}
        onCopy={copySetList}
      />
      <MetaSelectContainer>
        <label>Metadata:</label>
        <Select
          value={addFieldKey ? ADD_FIELDS.find(field => field.key === addFieldKey) ? { value: addFieldKey, label: ADD_FIELDS.find(field => field.key === addFieldKey).label } : null : null}
          onChange={(option) => onAddFieldChange(option ? option.value : '')}
          options={ADD_FIELDS.map(addField => ({ value: addField.key, label: addField.label }))}
          placeholder='Select metadata...'
          isClearable
        />
      </MetaSelectContainer>
      {_map(computeNumbering(getEntries(setlist)), ({ entry, songNumber }, idx) => {
        if (entry.type === 'note') {
          if (entry.isBreak) {
            return <BreakSeparator key={`break-${idx}`}>{entry.text || 'Set Break'}</BreakSeparator>
          }
          return <NoteText key={`note-${idx}`}>{entry.text}</NoteText>
        }
        return (
          <Song
            key={entry._id}
            song={entry}
            setlistid={useSetlistId}
            addFieldKey={addFieldKey}
            songNumber={songNumber}
          />
        )
      })}

      {setlist && <Duration songs={getEntries(setlist).filter(e => e.type !== 'note')} />}
      {setlist && <CopySetList setlist={setlist} />}
      {setlist && <SetlistShareButton setlistId={useSetlistId} setlist={setlist} />}

      {/* Permissions Modal */}
      <PermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        contentType='setlist'
        contentId={useSetlistId}
        contentName={setlist.title}
      />
    </div>
  )
}

export default ViewSetList
